import * as autobind from 'autobind';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'redux-scaffolding-ts';
import { Input, Message, Icon, Grid } from 'semantic-ui-react';
import { PillarDto, PillarsStore, PlanitPlannerTypes } from '../../../../../../src/stores/configuration/profiles/pillars-store';
import { ItemState, OrderDefinition, Query } from '../../../../../../src/stores/dataStore';
import { CommandResult } from '../../../../../../src/stores/types';
import { nameof } from 'utils/object';
import { TableModel, TableView } from 'widgets/collections/table';
import { TextBoxFilter } from 'widgets/collections/table-filters/textbox-filter';
import ChangePillarView from './edit-pillar';
import NewPillarView from './new-pillar';
import { OptionsFilter } from 'widgets/collections/table-filters/option-filter';

export interface PillarsListProps extends WithTranslation, RouteComponentProps {
  pillars: PillarsStore;
}

export interface PillarsListState {
  query: Query;
  newPillarShown: boolean;
  changePillarShown: boolean;
  activeFilters: string[];
  selectedItem: any;
  someFilterOpened: boolean;
}

@connect(['pillars', PillarsStore])
class PillarsListPage extends React.Component<PillarsListProps, PillarsListState> {
  handleOnEnterKeydown = item => {
    this.setState({ selectedItem: item }, () => this.onEditItem());
  };

  handleOnRowDoubleClick = item => {
    this.handleOnEnterKeydown(item);
  };

  constructor(props) {
    super(props);
    this.state = {
      query: { searchQuery: '', orderBy: [{ direction: 'Descending', field: 'modifiedOn', useProfile: false }], skip: 0, take: 10 },
      newPillarShown: false,
      changePillarShown: false,
      activeFilters: [],
      selectedItem: null,
      someFilterOpened: false
    };
  }

  componentDidMount() {
    this.load();
  }

  @autobind
  private load() {
    this.props.pillars.getAllAsync(this.state.query);
  }

  @autobind
  private handleOrderBy(orderBy: OrderDefinition[]) {
    this.setState(
      {
        query: Object.assign(this.state.query, {
          orderBy: [...orderBy, { direction: 'Descending', field: 'modifiedOn', useProfile: false }]
        })
      },
      this.load
    );
  }

  @autobind
  private handlePageChange(skip: number, take: number) {
    this.setState({ query: Object.assign(this.state.query, { skip, take }) }, () => {
      this.load();
    });
  }

  @autobind
  private async onSaveRow(item: PillarDto, state: ItemState): Promise<CommandResult<any>> {
    if (state !== 'New') {
      await this.props.pillars.saveAsync(item, state);
    }
    return { isSuccess: true, items: [], messages: [] };
  }

  @autobind
  private async onDelete(item: PillarDto, state: ItemState): Promise<CommandResult<any>> {
    if (state !== 'New') {
      await this.props.pillars.deleteAsync(item.id, state);
    }
    return { isSuccess: true, items: [], messages: [] };
  }

  @autobind
  private handleFilterChange(filters: { id: string; filter: any }[]) {
    const filter = filters.map(f => f.filter);
    const activeFilters = filters.map(f => f.id);

    const query = Object.assign(this.state.query, { filter, skip: 0 });
    this.setState({ query, activeFilters }, () => this.load());
  }

  @autobind
  private onNewItem() {
    this.setState({ newPillarShown: true });
  }

  @autobind
  private onNewItemClosed() {
    this.setState({ newPillarShown: false });
    this.load();
  }

  @autobind
  private onEditItem() {
    this.setState({ changePillarShown: true });
  }

  @autobind
  private onEditItemClosed() {
    this.setState({ changePillarShown: false });
    this.load();
  }

  @autobind
  private getOptionDescription(option?: PlanitPlannerTypes) {
    if (option === PlanitPlannerTypes.plannerTFT) return 'Planner TFT';
    if (option === PlanitPlannerTypes.plannerMTC) return 'Planner MTC';

    return '';
  }

  handleOnActivateFilter = (visible: boolean) => {
    this.setState({ someFilterOpened: visible });
  };

  public render() {
    const { t } = this.props as any;
    const { activeFilters, someFilterOpened } = this.state;

    const tableModel = {
      columns: [
        {
          title: t('Pillar'),
          tooltipRenderer: false,
          renderer: data => <span>{data.name}</span>,
          editor: (data, onChange) => (
            <Input
              value={data.name}
              fluid
              onChange={(e, { value }) => {
                data.name = value;
                onChange();
              }}
            />
          ),
          selectableHeader: true,
          headerRenderer: (title: string, onFilter, onClear) => (
            <TextBoxFilter
              filterTitle={t('Filter by Pillar')}
              triggerTitle={title}
              onFilter={value => onFilter(nameof<PillarDto>('name'), `contains(${nameof<PillarDto>('name')}, '${value}')`)}
              onClear={() => onClear(nameof<PillarDto>('name'))}
              active={activeFilters.includes(nameof<PillarDto>('name'))}
              onActivate={this.handleOnActivateFilter}
            />
          ),
          sortDefinition: {
            field: nameof<PillarDto>('name'),
            useProfile: false
          }
        },
        {
          title: t('Managed By'),
          tooltipRenderer: false,
          renderer: data => <span>{this.getOptionDescription(data.managedBy)}</span>,
          selectableHeader: true,
          headerRenderer: (title: string, onFilter, onClear) => (
            <OptionsFilter
              filterTitle={t('Filter by Managed By')}
              options={[
                { text: 'Planner TFT', value: 'PlannerTFT' },
                { text: 'Planner MTC', value: 'PlannerMTC' }
              ]}
              triggerTitle={title}
              onFilter={value => onFilter(nameof<PillarDto>('managedBy'), `managedBy eq '${value || ''}'`)}
              onClear={() => onClear(nameof<PillarDto>('managedBy'))}
              active={activeFilters.includes(nameof<PillarDto>('managedBy'))}
              onActivate={this.handleOnActivateFilter}
            />
          ),
          sortDefinition: {
            field: nameof<PillarDto>('managedBy'),
            useProfile: false
          }
        }
      ],
      data: this.props.pillars.state
    } as TableModel<PillarDto>;
    return (
      <>
        <Grid className="event-types-list-grid">
          {this.props.pillars.state.result && !this.props.pillars.state.result.isSuccess && (
            <Grid.Row className="event-types-list-error-row">
              <Message
                className="error-message__style"
                icon="exclamation circle"
                error
                header={t('An error ocurred')}
                list={this.props.pillars.state.result.messages.map(o => o.body)}
              />
            </Grid.Row>
          )}
          <Grid.Row className="event-types-list-items-row request-list__table-view">
            <TableView
              /////////////////For build table keyboard navegation/////////////////
              selectable={!this.state.newPillarShown && !this.state.changePillarShown}
              maxSelection={1}
              onHideCheckbox={true}
              selectionType={'allRow'}
              onEnterKeydown={this.handleOnEnterKeydown}
              onRowDoubleClick={this.handleOnRowDoubleClick}
              preventEnterKeyDownEvent={someFilterOpened}
              //showActionsConfirmModal={true}
              /////////////////For build table keyboard navegation/////////////////
              model={tableModel}
              extraActions={[
                {
                  content: (
                    <>
                      <Icon name="edit" />
                      &nbsp;{t('Edit')}
                    </>
                  ),
                  onClick: item => {
                    this.setState({ selectedItem: item }, () => this.onEditItem());
                  }
                }
              ]}
              onOrderByChanged={this.handleOrderBy}
              onNewItem={this.onNewItem}
              onRefresh={this.load}
              canEdit={false}
              canDelete={true}
              onDeleteRow={this.onDelete}
              onSaveRow={this.onSaveRow}
              onPageChange={this.handlePageChange}
              onFilterChange={this.handleFilterChange}
              canCreateNew={true}
              createNewButtonTitle={t('Add Pillar')}
            ></TableView>
          </Grid.Row>
        </Grid>
        {(this.state.newPillarShown && <NewPillarView onClose={this.onNewItemClosed} {...this.props} />) ||
          (this.state.changePillarShown && <ChangePillarView onClose={this.onEditItemClosed} currentDto={this.state.selectedItem} />)}
      </>
    );
  }
}

export default withTranslation()(PillarsListPage);
